import {
  alpha,
  Box,
  FormControl as MuiFormControl,
  FormHelperText as MuiFormHelperText,
  FormLabel as MuiFormLabel,
  InputAdornment as MuiInputAdornment,
  InputBase as MuiInputBase,
  Typography,
  styled,
  InputAdornmentProps,
} from '@mui/material'

import IconButton from '../IconButton'
import ThemeCenter from 'theme'

export interface IBaseInputProps {
  starticon?: JSX.Element
  endicon?: JSX.Element
  endiconchange?: number
  success?: boolean
}

export const FormControl = styled(MuiFormControl)<{ width?: string; type?: string }>(
  ({ width, type }) => ({
    width: width ? width : type === 'number' ? 144 : '100%',
    backgroundColor: 'transparent',
  }),
)

export const HeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'bottom',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
  minHeight: theme.spacing(1),
  width: '100%',
}))

export const RequiredContent = styled(Typography)(({ theme }) => ({
  display: 'flex',
  position: 'absolute',
  left: -7,
  fontWeight: 600,
  fontSize: 12,
  // lineHeight: "12px",
  color: theme.palette.primary.main,
}))

export const FormLabel = styled(MuiFormLabel)(({ theme }) => ({
  fontWeight: 500,
  fontSize: 12,
  lineHeight: '16px',
  color: theme.palette.grey[800],
  '&.Mui-disabled, &.Mui-error, &.Mui-focused': {
    color: theme.palette.grey[800],
  },
}))

export const AddLink = styled(Typography)(({ theme }) => ({
  cursor: 'pointer',
  fontWeight: 600,
  fontSize: 10,
  lineHeight: 2,
  color: theme.palette.primary.main,
  '&:hover': {
    color: theme.palette.primary.dark,
  },
}))

export const StartIconContainer = styled(MuiInputAdornment)(() => ({
  cursor: 'default',
  display: 'flex',
  alignItems: 'center',
  marginRight: 4,
}))

export const EndIconContainer = styled(MuiInputAdornment)<
  { passwordhidden: number } & InputAdornmentProps
>(({ hidden }) => ({
  cursor: hidden ? 'cursor' : 'pointer',
  display: 'flex',
  alignItems: 'center',
  marginLeft: 4,
}))

export const OutlinedInput = styled(MuiInputBase)<IBaseInputProps>(
  ({ theme, multiline, value, starticon, endiconchange }) => ({
    width: '100%',
    alignItems: multiline ? 'flex-start' : 'center',
    minHeight: 32,
    color: '#464646',
    fontSize: 12,
    fontWeight: 400,
    gap: '6px',
    lineHeight: 2,

    padding: starticon
      ? theme.spacing(0, 1, 0, 1.25)
      : theme.spacing(0.5, 1, 0.5, 2),
    borderRadius: 8,
    border: '1px solid #EFEFEF',
    backgroundColor: 'white',

    '&:hover': {
      border: '1px solid #EFEFEF',
    },

    '& > .MuiInputAdornment-positionStart': {
      pointerEvents: 'none',
      color: theme.palette.grey[800],
    },

    '& > .MuiInputAdornment-positionEnd': {
      display: 'flex',
      alignItems: 'center',
      color: theme.palette.grey[300],
      width: '24px',
      height: '24px',

      '& span': {
        display: 'flex',
        alignItems: 'center',
        height: '24px',
      },
      '&:hover': {
        color: endiconchange
          ? theme.palette.grey[800]
          : theme.palette.grey[300],
      },
    },

    '& > .MuiInputBase-input': {
      padding: 0,
      color: '#464646',
      fontWeight: 400,
      fontSize: 12,

      fontStyle: value === '' && 'italic',
      '&.Mui-disabled': {
        backgroundColor: 'transparent',
      },
      '&::placeholder': {
        lineHeight: 1.5,
        textOverflow: 'ellipsis !important',
        color: '#464646',
      },
    },

    '&.Mui-disabled': {
      boxShadow: 'none',
      color: theme.palette.grey[500],
      backgroundColor: theme.palette.grey[200],
      '&:hover': {
        boxShadow: 'none',
      },
      '& > .MuiInputAdornment-positionStart': {
        cursor: 'default',
        color: theme.palette.grey[500],
        '&:hover': {
          color: theme.palette.grey[500],
        },
      },
    },

    '&.Mui-error:not(.Mui-disabled)': {
      boxShadow: `inset 0 0 0 2px ${theme.palette.error.main}`,
      '& > .MuiInputAdornment-positionStart': {
        color: theme.palette.error.main,
      },
      '& > .MuiInputAdornment-positionEnd': {
        color: theme.palette.error.light,
        '&:hover': {
          color: endiconchange
            ? theme.palette.error.dark
            : theme.palette.error.light,
        },
      },
    },

    '&.Mui-focused:not(.Mui-error)': {
      boxShadow: 'none',
      border: '1px solid #EFEFEF',
      '& > .MuiInputAdornment-positionStart': {
        color: '#464646',
      },
    },
  }),
)

export const OutlinedNumberInput = styled(OutlinedInput)(
  ({ theme }: { theme: typeof ThemeCenter }) => ({
    width: '80px',
    height: '32px',
    padding: theme.spacing(1),
  }),
)

export const FilledInput = styled(MuiInputBase)<IBaseInputProps>(
  ({ theme, multiline, value, success, type, starticon, endiconchange }) => ({
    width: '100%',
    alignItems: multiline ? 'baseline' : 'center',
    // minHeight: 40,
    backgroundColor: 'pink',
    color: success
      ? theme.palette.success.main
      : value
      ? theme.palette.grey[800]
      : theme.palette.grey[600],
    padding:
      type === 'number' || starticon
        ? theme.spacing(1)
        : theme.spacing(1, 1, 1, 2),
    borderRadius: theme.shape.borderRadius / 2,
    // backgroundColor: success
    //   ? theme.palette.success.lightest
    //   : theme.palette.secondary.lightest,
    '&:hover': {
      transition: 'all ease-in-out 0.3s',
      boxShadow: `0 12px 12px -12px ${alpha(theme.palette.common.black, 0.24)}`,
    },
    '& > .MuiInputAdornment-positionStart': {
      pointerEvents: 'none',
      color: success
        ? theme.palette.success.main
        : theme.palette.secondary.main,
    },
    '& > .MuiInputAdornment-positionEnd': {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      color: success ? theme.palette.success.main : theme.palette.grey[300],
      width: '24px',
      height: '24px',
      '& span': {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '24px',
        height: '24px',
      },
      '&:hover': {
        cursor: 'pointer',
        color: success
          ? endiconchange
            ? theme.palette.success.dark
            : theme.palette.success.main
          : endiconchange
          ? theme.palette.grey[800]
          : theme.palette.grey[300],
      },
    },
    '& > input': {
      padding: 0,
      fontWeight: 500,
      fontSize: 12,
      lineHeight: 2,
      fontStyle: value === '' && 'italic',
      '&.Mui-disabled': {
        backgroundColor: 'transparent',
      },
    },
    '&.Mui-disabled': {
      color: theme.palette.grey[600],
      '&:hover': {
        boxShadow: 'none',
      },
      '& > .MuiInputAdornment-positionStart': {
        cursor: 'default',
        color: theme.palette.grey[600],
        '&:hover': {
          color: theme.palette.grey[600],
        },
      },
    },
    '&.Mui-error:not(.Mui-disabled)': {
      color: theme.palette.error.dark,
      '&:hover': {
        boxShadow: 'none',
      },
      '& > .MuiInputAdornment-positionStart': {
        color: theme.palette.error.main,
      },
      '& > .MuiInputAdornment-positionEnd': {
        color: theme.palette.error.light,
        '&:hover': {
          color: endiconchange
            ? theme.palette.error.dark
            : theme.palette.error.light,
        },
      },
    },
    '&.Mui-focused:not(.Mui-error)': {
      color: success ? theme.palette.success.dark : theme.palette.primary.dark,
      '& > .MuiInputAdornment-positionStart': {
        color: success
          ? theme.palette.success.main
          : theme.palette.primary.main,
      },
      '& > .MuiInputAdornment-positionEnd': {
        color: success
          ? theme.palette.success.light
          : theme.palette.primary.light,
        '&:hover': {
          color: success
            ? endiconchange
              ? theme.palette.success.dark
              : theme.palette.success.main
            : endiconchange
            ? theme.palette.primary.dark
            : theme.palette.primary.light,
        },
      },
    },
  }),
)
export const FilledNumberInput = styled(FilledInput)(({ theme }) => ({
  width: '100%',
  marginLeft: theme.spacing(2),
  marginRight: theme.spacing(2),
  padding: theme.spacing(1),
}))

export const NumberButton = styled(IconButton)<{ variant: string }>(
  ({ theme, variant }) => ({
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.grey[800],
    boxShadow:
      variant === 'outlined'
        ? `inset 0 0 0 1px ${theme.palette.grey[200]}`
        : 'none',
    backgroundColor:
      variant === 'contained' ? theme.palette.secondary.light : 'default',
    '&:hover': {
      color: theme.palette.grey[800],
      boxShadow:
        variant === 'outlined' && `inset 0 0 0 1px ${theme.palette.grey[400]}`,
      backgroundColor: variant === 'contained' && theme.palette.secondary.light,
    },
    '&.Mui-disabled': {
      boxShadow: ' none',
      backgroundColor:
        variant === 'outlined'
          ? theme.palette.grey[200]
          : theme.palette.secondary.light,
    },
  }),
)

export const FooterContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'top',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
  width: '100%',
}))

export const ErrorText = styled(MuiFormHelperText)(({ theme }) => ({
  margin: 0,
  fontWeight: 600,
  fontSize: 10,
  lineHeight: 1.6,
  color: theme.palette.error.main,
  '&.Mui-error:not(.Mui-disabled)': {
    margin: 0,
    fontWeight: 600,
    fontSize: 10,
    lineHeight: 1.6,
    color: theme.palette.error.main,
  },
}))

export const CountText = styled(MuiFormHelperText)(({ theme }) => ({
  margin: 0,
  fontWeight: 600,
  fontSize: 10,
  lineHeight: 1.6,
  color: theme.palette.grey[600],
  '&.Mui-error:not(.Mui-disabled)': {
    margin: 0,
    fontWeight: 600,
    fontSize: 10,
    lineHeight: 1.6,
    color: theme.palette.error.main,
  },
}))
